import "../style.scss"
import "./styles.scss"

import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import { CircularProgress } from "@mui/material"
import Backdrop from "@mui/material/Backdrop"
import _get from "lodash/get"
import moment from "moment"
import React, { lazy, Suspense, useCallback, useEffect, useState } from "react"

import AccessControl from "../../../components/access/AccessControl"
import PasswordProtected from "../../../components/access/PasswordProtected"
import Error from "../../../components/error"
import EventMap from "../../../components/event-map"
import Head from "../../../components/head"
import { X_TF_ECOMMERCE } from "../../../components/header"
import Layout from "../../../components/layout"
import { ENV } from "../../../constants/env"
import { getEvent } from "../../../http_service"
import { deleteCookieByName, getCookieByName } from "../../../utils/cookies"
import { defaultThemeSettings, setTfCheckoutReactConfigs } from "../../../utils/tf-checkout-config"
import NotFoundPage from "../../404"
import BillingPage from "../../billing/billing-info"

const TicketsContainer = lazy(() => import("tf-checkout-react").then(module => ({ default: module.TicketsContainer })))

const cookie = require("cookie")

const isWindowDefined = typeof window !== "undefined"

setTfCheckoutReactConfigs()


const EventPage = ({ serverData }) => {
  const [eventData, setEventData] = useState({
    event: {},
    venue: {},
    slug: "",
    pk: "",
    tickets: []
  })

  const [uiState, setUiState] = useState({
    loading: false,
    showBillingInfo: false,
    isLoggedIn: false,
    isHydrated: false,
    queryPromoCode: "",
    showFloatingButton: false
  })

  const [checkoutData, setCheckoutData] = useState({})

  const eventSlug = serverData?.slug
  const eventId = serverData?.eventId
  const isPasswordProtected = serverData?.error && serverData?.message?.status === 401

  useEffect(() => {
      setUiState(prev => ({
        ...prev,
        isHydrated: true,
        isLoggedIn: !!getCookieByName(X_TF_ECOMMERCE),
        queryPromoCode: new URL(window.location.href).searchParams.get("r") || ""
      }))
  }, [])

  // Floating button visibility logic for mobile
  useEffect(() => {
    if (!isWindowDefined || window.innerWidth >= 768) return

    const baseWidth = window.innerWidth

    const handleScroll = () => {
      const { activeElement } = document
      const isBillingFieldFocused =
        uiState.showBillingInfo &&
        activeElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(activeElement.tagName) &&
        activeElement.closest(".billing-info")

      // Avoid UI state churn while iOS keyboard opens/closes on billing fields.
      if (isBillingFieldFocused) {
        return
      }

      // Check if billing info is showing
      if (uiState.showBillingInfo) {
        const billingInfo = document.querySelector('.billing-info')
        if (!billingInfo) {
          setUiState(prev => ({ ...prev, showFloatingButton: true }))
          return
        }
        const billingRect = billingInfo.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        // Show button if billing info is not in view (top is below viewport or mostly above it)
        const isInView = billingRect.top < viewportHeight * 0.8 && billingRect.bottom > 100
        setUiState(prev => ({ ...prev, showFloatingButton: !isInView }))
      } else {
        // Check tickets wrapper visibility
        const ticketsWrapper = document.querySelector('.tickets-wrapper')
        if (!ticketsWrapper) {
          setUiState(prev => ({ ...prev, showFloatingButton: false }))
          return
        }
        const ticketsRect = ticketsWrapper.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        // Show button if tickets wrapper is not in view
        const isInView = ticketsRect.top < viewportHeight * 0.8 && ticketsRect.bottom > 100
        setUiState(prev => ({ ...prev, showFloatingButton: !isInView }))
      }
    }

    const handleResize = () => {
      // Ignore height-only changes — these are caused by the mobile keyboard
      // opening/closing and should not trigger a re-render that disrupts input focus
      if (window.innerWidth !== baseWidth) {
        handleScroll()
      }
    }

    handleScroll() // Check on mount
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [uiState.isHydrated, uiState.showBillingInfo])

  const scrollToTicketsOrBilling = () => {
    if (uiState.showBillingInfo) {
      const billingInfo = document.querySelector('.billing-info')
      if (billingInfo) {
        const yOffset = -20 // 20px offset from top
        const y = billingInfo.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    } else {
      const ticketsWrapper = document.querySelector('.tickets-wrapper')
      if (ticketsWrapper) {
        const yOffset = -20 // 20px offset from top
        const y = ticketsWrapper.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }
  }

  // Scroll to billing info when it becomes visible
  useEffect(() => {
    if (uiState.showBillingInfo && isWindowDefined) {
      // Small delay to ensure the element is rendered
      setTimeout(() => {
        const billingInfo = document.querySelector('.billing-info')
        if (billingInfo) {
          const yOffset = -20 // 20px offset from top for better visibility
          const y = billingInfo.getBoundingClientRect().top + window.pageYOffset + yOffset
          window.scrollTo({ top: y, behavior: 'smooth' })
        }
      }, 150)
    }
  }, [uiState.showBillingInfo])

  useEffect(() => {
    if (serverData) {
      setEventData({
        event: _get(serverData, "event.event", {}),
        venue: _get(serverData, "event.venue", {}),
        slug: _get(serverData, "slug", ""),
        pk: _get(serverData, "pk", "")
      })
    }
  }, [serverData])

  const handleCartSuccessRedirect = useCallback((data, event_id = "") => {
    if (!isWindowDefined) {
      return
    }
    setCheckoutData(data)

    if(data.skip_billing_page) {
      window.location.href = `/billing/checkout?event_id=${event_id}`
    } else {
      setUiState(prev => ({ ...prev, showBillingInfo: true }))
    }
  }, [])


  if (serverData.showErrorPage) {
    return <NotFoundPage />
  }

  if (serverData.storedPassword !== ENV.TTF_ACCESS && serverData.host?.includes("tickets-staging.manacommon.com")) {
    return <AccessControl />
  }

  const { name, start_date, end_date, image_full_url, description, id, prereg_enabled, prereg_ended } =
    isPasswordProtected ? eventData.event : _get(serverData, "event.event", {})

  // Fix description images to respect their width/height attributes
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!description || !isWindowDefined) return

    const descriptionContainer = document.querySelector('.description-container')
    if (!descriptionContainer) return

    const images = descriptionContainer.querySelectorAll('img[width], img[height]')
    images.forEach(img => {
      const width = img.getAttribute('width')
      const height = img.getAttribute('height')

      if (width) {
        img.style.width = `${width}px`
        img.style.maxWidth = '100%'
      }
      if (height) {
        img.style.height = `${height}px`
      }
    })
  }, [description])

  const date = moment(start_date).format("ddd DD MMM YYYY")
  const startHour = moment(start_date).format("HH:mm")
  const endHour = moment(end_date).format("HH:mm")

  const getAuthorizedEvent = async () => {
    setUiState(prev => ({ ...prev, loading: true }))
    try {
      const response = await getEvent(eventData.slug, eventData.pk)
      setEventData({
        event: _get(response, "event"),
        venue: _get(response, "venue", {}),
        slug: _get(response, "slug", ""),
        pk: _get(response, "pk", "")
      })
    } catch (e) {
    } finally {
      setUiState(prev => ({ ...prev, loading: false }))
    }
  }

  if (uiState.loading) {
    return (
      <Backdrop sx={{ color: "#fff", backgroundColor: "transparent", zIndex: 1205 }} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )
  }

  if (serverData.error && Object.keys(eventData.event).length === 0) {
    if (serverData.message.status === 401) {
      return (
        <Layout>
          <PasswordProtected
            slug={eventData.slug || eventSlug}
            pk={eventData.pk}
            id={id || eventId}
            getAuthorizedEvent={getAuthorizedEvent}
          />
        </Layout>
      )
    }
    return <Error />
  }

  const handleReserveButtonClick = () => {
    if (isWindowDefined) {
      window.location.href = `/events/${eventData.slug || eventSlug}/seat-map?event_id=${id || eventId}`
    }
  }

  const cartItems = Array.isArray(checkoutData.cart) ? checkoutData.cart : []
  const totalTicketsSelected = cartItems.reduce((total, item) => total + Number(item.quantity || 0), 0)
  const totalTicketsAmount = cartItems.reduce((total, item) => total + Number(item.price || 0), 0)
  const formattedTotalTicketsAmount = totalTicketsAmount.toFixed(2)
  // Temp redirection
  if (_get(serverData, "event.event.skip_details_page", false)) {
    handleReserveButtonClick()
    return null
  }

  const eventDescription = description
    ? description.replace(/<[^>]*>/g, '').substring(0, 160) // Strip HTML and limit to 160 chars
    : `Get tickets for ${name} on ${date} at ${eventData.venue.name}. Buy your tickets securely on TFSports.`

  const eventKeywords = `${name}, sports tickets, buy tickets, ${eventData.venue.name}, sports events, TFSports`

  return (
    <>
      <Head title={`${name} - Buy Tickets | TFSports`} description={eventDescription} image={image_full_url} keywords={eventKeywords} />
      <Layout>
        <div className="event-details-container" itemScope itemType="https://schema.org/Event">
          {/* Mobile event image - shown only on mobile */}
          <div className="mobile-event-image-container">
            {uiState.isHydrated && image_full_url && (
              <img
                itemProp="image"
                src={image_full_url}
                alt="Event flyer"
                onError={({ currentTarget }) => {
                  currentTarget.setAttribute('style', 'display: none;')
                }}
              />
            )}
          </div>

          {/* Event info header */}
          <div className="info-container">
            <h1 className="name" itemProp="name">
              {name}
            </h1>
            <p className="date-venue">
              <span itemProp="startDate">{ date } </span>
              <span itemProp="startHour">{ startHour } - </span>
              <span itemProp="endHour">{ endHour }</span>
            </p>
            <p className="date-venue">
              <span itemProp="location" itemScope itemType="https://schema.org/Place">
                <LocationOnOutlinedIcon />
                <span itemProp="name">{eventData.venue.name}</span>
              </span>
            </p>
          </div>

          {/* Main content container */}
          <div className="main-content-container">
            {/* Description column - LEFT */}
            <div itemProp="description" className="description-container">
              {description ? (
                <div dangerouslySetInnerHTML={{ __html: description }} />
              ) : (
                <div className="no-description">
                  <p>No description available yet.</p>
                </div>
              )}

              {/* Map below description */}
              <EventMap venue={isPasswordProtected ? eventData.venue : _get(serverData, "event.venue", {})} />
            </div>

            {/* Tickets and image column - RIGHT */}
            <div className="mn__event-tickets-container">
              {/* Desktop event image - ABOVE tickets */}
              <div className="event-image-container">
                {uiState.isHydrated && image_full_url && (
                  <img
                    itemProp="image"
                    src={image_full_url}
                    alt="Event flyer"
                    onError={({ currentTarget }) => {
                      currentTarget.setAttribute('style', 'display: none;')
                    }}
                  />
                )}
              </div>

              {/* Tickets section - BELOW image */}
              <Suspense fallback={<CircularProgress color="inherit" />}>
                <>
                  <div
                    className="tickets-wrapper"
                    style={{
                      width: "100%",
                      visibility: uiState.showBillingInfo ? "hidden" : "visible",
                      height: uiState.showBillingInfo ? "0" : "auto"
                    }}
                  >
                      <TicketsContainer
                        theme="light"
                        themeOptions={defaultThemeSettings}
                        eventId={id}
                        disablePreRegistration={!prereg_enabled}
                        handleNotInvitedModalClose={() => {}}
                        handleInvalidLinkModalClose={() => {}}
                        onAddToCartSuccess={data => {
                          handleCartSuccessRedirect(data, id)
                        }}
                        isPromotionsEnabled={eventData.event?.is_promotions_enabled}
                        isAccessCodeEnabled={eventData.event?.is_access_code}
                        onLogoutSuccess={() => {
                          if (isWindowDefined) {
                            window.location.href = "/"
                          }
                        }}
                        queryPromoCode={uiState.queryPromoCode}
                        hideSessionButtons={true}
                        enableAddOns={true}
                        showGroupNameBlock={true}
                        onReserveButtonClick={handleReserveButtonClick}
                        tableTicketsHeaderComponent={<div className="tickets-container-header">RESERVE TABLES</div>}
                        onPendingVerification={() => {
                          if (isWindowDefined) {
                            window.location.replace(`/verification`)
                          }
                        }}
                        onGetProfileDataError={error => {
                          if (error.response.status === 401) {
                            deleteCookieByName(X_TF_ECOMMERCE)
                            if (isWindowDefined) {
                              window.localStorage.removeItem("user_data")
                            }
                          }
                        }}
                      />
                    </div>
                    {uiState.showBillingInfo && (
                      <div className="billing-controls-section">
                        <div className="selected-tickets-info">
                          {totalTicketsSelected > 0 && (
                            <span className="selection-summary">
                              {totalTicketsSelected} ticket{totalTicketsSelected > 1 ? 's' : ''} selected • {checkoutData.currencySymbol}{formattedTotalTicketsAmount}
                            </span>
                          )}
                        </div>
                        <button
                          className="edit-tickets-btn"
                          onClick={() => setUiState(prev => ({ ...prev, showBillingInfo: false }))}
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </>
                  {uiState.showBillingInfo && (
                    <BillingPage />
                  )}
                </Suspense>
            </div>
          </div>
        </div>

        {/* Floating Get Tickets/Checkout Button - Mobile Only */}
        {uiState.isHydrated && uiState.showFloatingButton && (
          <button type="button" className="floating-get-tickets-btn" onClick={scrollToTicketsOrBilling}>
            {uiState.showBillingInfo ? 'Go to Checkout' : 'Get Tickets'}
          </button>
        )}
      </Layout>
    </>
  )
}

export async function getServerData(props) {
  const cookies = props.headers.get("cookie")
  let storedPassword = ""
  if (cookies) {
    storedPassword = cookie.parse(cookies)?.["ttf-access"] || ""
  }

  let slug = props.params?.slug || ""

  if (!slug && props.url) {
    const urlMatch = props.url.match(/\/events\/([^/]+)/)
    if (urlMatch) {
      const [, extractedSlug] = urlMatch
      if (extractedSlug) {
        slug = extractedSlug
      }
    }
  }

  // Defensive checks for slug
  if (!slug) {
    console.error('[SSR] Missing slug in params and could not extract from URL:', props)
    return {
      status: 404,
      props: {
        showErrorPage: true
      }
    }
  }

  const {
    query: { pk, eventId }
  } = props

  try {
    const event = await getEvent(slug, pk)
    const { end_date } = event.event

    const endDateUnindex = moment(end_date).add(2, "days")
    const isAfterEndDate = moment().isAfter(endDateUnindex)

    if (isAfterEndDate) {
      return {
        status: 404,
        props: {
          showErrorPage: true
        }
      }
    }

    return {
      props: {
        event,
        slug,
        pk,
        eventId,
        storedPassword,
        host: props.headers.get("host")
      }
    }
  } catch (error) {
    return {
      props: {
        error: true,
        message: error,
        slug,
        pk,
        eventId: error?.response?.data?.event_id
      }
    }
  }
}

export default EventPage
